/**
 * ClockInFlow — Driver Portal clock-in component
 * Extracted from DriverApp.jsx (V2-03.12g)
 *
 * Three clock-in type variants: driver, jumper, other
 * All validation and request payloads preserved exactly.
 */

import React, { useState } from 'react';
import { C, FONT, GLASS, GLASS_STRONG, INPUT, BTN, BTN_DISABLED } from '../driver-tokens.js';
import { apiPost } from '../auth/driver-session.js';

function ClockInFlow({ onDone }: { onDone: () => void }) {
  var [clockType, setClockType] = useState<string | null>(null);
  var [route, setRoute] = useState("");
  var [serviceCode, setServiceCode] = useState("");
  var [jumpingFor, setJumpingFor] = useState("");
  var [otherNote, setOtherNote] = useState("");
  var [terminal, setTerminal] = useState("");
  var [truckNumber, setTruckNumber] = useState("");
  var [startingMileage, setStartingMileage] = useState("");
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState("");

  function doClockIn(payload: Record<string, unknown>) {
    setSaving(true); setError("");
    apiPost<{ success?: boolean; message?: string; error?: string }>("/api/clock/in", payload).then(function(data) {
      setSaving(false);
      if (data.success) { onDone(); }
      else { setError(data.message || data.error || "Clock-in failed"); }
    }).catch(function() { setSaving(false); setError("Connection error"); });
  }

  function handleDriverSubmit() {
    if (!terminal.trim()) { setError("Terminal is required"); return; }
    if (!route.trim()) { setError("Route is required"); return; }
    if (!serviceCode.trim()) { setError("Service code is required"); return; }
    if (!truckNumber.trim()) { setError("Truck number is required"); return; }
    if (!startingMileage.trim() || parseInt(startingMileage) < 0) { setError("Starting mileage is required"); return; }
    doClockIn({
      clock_in_type: "driver",
      route_number_declared: route.trim(),
      service_code_declared: serviceCode.trim(),
      route_number: route.trim(),
      station_number: terminal.trim(),
      truck_number: truckNumber.trim(),
      starting_mileage: parseInt(startingMileage)
    });
  }

  function handleJumperSubmit() {
    if (!jumpingFor.trim()) { setError("Driver ID is required"); return; }
    doClockIn({ clock_in_type: "jumper", jumping_for_employee_id: jumpingFor.trim() });
  }

  function handleOtherSubmit() {
    if (otherNote.trim().length < 5) { setError("Note must be at least 5 characters"); return; }
    doClockIn({ clock_in_type: "other", other_note: otherNote.trim() });
  }

  // Type picker
  if (!clockType) {
    return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
      React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20 } },
        React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, textAlign: "center", marginBottom: 20 } }, "Clock In"),
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 12, color: C.silver, textAlign: "center", marginBottom: 20 } }, "What type of shift?"),
        ["driver", "jumper", "other"].map(function(t) {
          var labels: Record<string, string> = { driver: "Driver", jumper: "Jumper", other: "Other" };
          var descs: Record<string, string> = { driver: "Regular route delivery", jumper: "Jumping for another driver", other: "Training, meeting, etc." };
          return React.createElement("button", {
            key: t, type: "button",
            style: { ...GLASS, width: "100%", padding: 16, marginBottom: 10, textAlign: "left", cursor: "pointer", minHeight: 56, display: "flex", flexDirection: "column" },
            onClick: function() { setClockType(t); setError(""); }
          },
            React.createElement("div", { style: { fontFamily: FONT.body, fontSize: 16, color: C.text, fontWeight: 600 } }, labels[t]),
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted } }, descs[t])
          );
        }),
        React.createElement("button", { type: "button", style: { ...GLASS, width: "100%", padding: 14, color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: "pointer", marginTop: 8, minHeight: 48, textAlign: "center" }, onClick: onDone }, "Cancel")
      )
    );
  }

  // Driver form
  if (clockType === "driver") {
    var LABEL = { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 };
    var FIELD = { marginBottom: 12 };
    return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
      React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20, maxHeight: "90vh", overflowY: "auto" } },
        React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, textAlign: "center", marginBottom: 20 } }, "Driver Clock In"),
        React.createElement("div", { style: FIELD },
          React.createElement("div", { style: LABEL }, "Terminal *"),
          React.createElement("input", { style: INPUT, value: terminal, onChange: function(e) { setTerminal(e.target.value); }, placeholder: "e.g. Lawrenceburg", autoFocus: true })
        ),
        React.createElement("div", { style: FIELD },
          React.createElement("div", { style: LABEL }, "Route *"),
          React.createElement("input", { style: INPUT, value: route, onChange: function(e) { setRoute(e.target.value); }, placeholder: "e.g. 0210" })
        ),
        React.createElement("div", { style: FIELD },
          React.createElement("div", { style: LABEL }, "Service Code *"),
          React.createElement("input", { style: INPUT, value: serviceCode, onChange: function(e) { setServiceCode(e.target.value); }, placeholder: "e.g. P1" })
        ),
        React.createElement("div", { style: FIELD },
          React.createElement("div", { style: LABEL }, "Truck Number *"),
          React.createElement("input", { style: INPUT, value: truckNumber, onChange: function(e) { setTruckNumber(e.target.value); }, placeholder: "e.g. 1234567 or V805011" })
        ),
        React.createElement("div", { style: { marginBottom: 20 } },
          React.createElement("div", { style: LABEL }, "Starting Mileage *"),
          React.createElement("input", { type: "number", style: INPUT, value: startingMileage, onChange: function(e) { setStartingMileage(e.target.value); }, placeholder: "e.g. 10254", inputMode: "numeric" })
        ),
        error && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, textAlign: "center", marginBottom: 12 } }, error),
        React.createElement("button", { type: "button", style: saving ? BTN_DISABLED : { ...BTN, background: C.green, color: C.navy }, disabled: saving, onClick: handleDriverSubmit }, saving ? "Clocking in..." : "Clock In"),
        React.createElement("button", { type: "button", style: { ...GLASS, width: "100%", padding: 14, color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: "pointer", marginTop: 8, minHeight: 48, textAlign: "center" }, onClick: function() { setClockType(null); setError(""); } }, "\u2190 Back")
      )
    );
  }

  // Jumper form
  if (clockType === "jumper") {
    return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
      React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20 } },
        React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, textAlign: "center", marginBottom: 20 } }, "Jumper Clock In"),
        React.createElement("div", { style: { marginBottom: 20 } },
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 } }, "Jumping for (Driver ID) *"),
          React.createElement("input", { style: INPUT, value: jumpingFor, onChange: function(e) { setJumpingFor(e.target.value); }, placeholder: "7-digit Driver ID", inputMode: "numeric", autoFocus: true })
        ),
        error && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, textAlign: "center", marginBottom: 12 } }, error),
        React.createElement("button", { type: "button", style: saving ? BTN_DISABLED : { ...BTN, background: C.green, color: C.navy }, disabled: saving, onClick: handleJumperSubmit }, saving ? "Clocking in..." : "Clock In"),
        React.createElement("button", { type: "button", style: { ...GLASS, width: "100%", padding: 14, color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: "pointer", marginTop: 8, minHeight: 48, textAlign: "center" }, onClick: function() { setClockType(null); setError(""); } }, "\u2190 Back")
      )
    );
  }

  // Other form
  return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
    React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20 } },
      React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, textAlign: "center", marginBottom: 20 } }, "Other Clock In"),
      React.createElement("div", { style: { marginBottom: 20 } },
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 } }, "Note (min 5 chars) *"),
        React.createElement("input", { style: INPUT, value: otherNote, onChange: function(e) { setOtherNote(e.target.value); }, placeholder: "Training, meeting, etc.", autoFocus: true })
      ),
      error && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, textAlign: "center", marginBottom: 12 } }, error),
      React.createElement("button", { type: "button", style: saving ? BTN_DISABLED : { ...BTN, background: C.green, color: C.navy }, disabled: saving, onClick: handleOtherSubmit }, saving ? "Clocking in..." : "Clock In"),
      React.createElement("button", { type: "button", style: { ...GLASS, width: "100%", padding: 14, color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: "pointer", marginTop: 8, minHeight: 48, textAlign: "center" }, onClick: function() { setClockType(null); setError(""); } }, "\u2190 Back")
    )
  );
}

export default ClockInFlow;
